import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
// stahlia-frontend/src/pages/warehouse/InventurZaehlenPage.tsx
// Inventur-Zaehlmaske (Spec-Merkliste ③.4/③.8/③.10): Platz-Code scannen, je aktiver Position
// entweder Sichtvergleich (verschlossen) oder Menge erfassen (ausgepackt), Plausibilitaets-
// Warnung ist Nie-Blockieren (Bestaetigung speichert trotzdem), globales "Platz ist LEER",
// im Produkt-Modus zusaetzlich Handregal-Erfassung ohne Platz-Bezug.
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import { useWedgeScanner } from '@/hooks/useScanner'
import { classifyScanFrontend } from '@/lib/scan'
import { qtyWarning } from '@/lib/inventur-form'
import { newIdempotencyKey } from '@/lib/offline-queue'
import {
  fetchPlaceInfo, useInventurDetail, useRecordCount,
  type CountPlaceInfo, type CountInput,
} from '@/api/inventur'
import { ApiError, isTransportError } from '@/api/client'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'

type PositionRow = CountPlaceInfo['positions'][number]
type CountEntry = NonNullable<CountInput['entries']>[number]
// A-C6/C-I7 (Welle S2): `sendKey` traegt EINEN pro Platz-Scan stabilen
// idempotency_key — bleibt ueber Retries desselben Platzes (fehlgeschlagener
// Sammel-POST, erneuter Klick) unveraendert, ein NEUER Scan erzeugt einen
// neuen Key (das ist fachlich ein neuer Sende-Versuch, kein Retry).
type Phase = { step: 'idle' } | { step: 'place'; barcode: string; info: CountPlaceInfo; sendKey: string }
// Lokale Entscheidung je Position (F2-Fix, Codex-Final-Review): wird gesammelt,
// bis JEDE aktive Position des Platzes eine Entscheidung hat — erst dann geht
// EIN Sammel-POST raus. Sonst markiert schon die erste Palette den ganzen
// Mischplatz serverseitig als gezählt (openPlaceIds) und weitere Paletten
// werden beim naechsten Scan uebersprungen.
type LocalDecision = { kind: 'confirmed' } | { kind: 'counted'; qty: number } | { kind: 'missing' }

export default function InventurZaehlenPage() {
  const { id } = useParams()
  return <InventurZaehlenSession key={id} sessionId={Number(id)} />
}

type HandregalRequest = { sessionId: number; body: CountInput & { handregal: { merchant_sku: string; counted_qty: number }; idempotency_key: string } }
const storageKey = (id: number) => `stahlia.inventur.handregal.v1.${id}`
const storageError = 'Browser-Speicher nicht verfügbar – Zählung kann nicht sicher gesendet werden'

function readHandregalRequest(sessionId: number): HandregalRequest | null {
  try {
    const raw = sessionStorage.getItem(storageKey(sessionId))
    if (raw === null) return null
    try {
      const value: unknown = JSON.parse(raw)
      if (typeof value !== 'object' || value === null) throw new Error('Ungültiger Auftrag')
      const request = value as HandregalRequest
      const body = request.body
      const handregal = body?.handregal
      if (request.sessionId !== sessionId || !Number.isSafeInteger(sessionId) || sessionId <= 0 ||
          !body || Object.keys(body).sort().join(',') !== 'handregal,idempotency_key' ||
          !handregal || Object.keys(handregal).sort().join(',') !== 'counted_qty,merchant_sku' ||
          typeof handregal.merchant_sku !== 'string' || !handregal.merchant_sku.trim() ||
          typeof handregal.counted_qty !== 'number' || !Number.isFinite(handregal.counted_qty) || handregal.counted_qty < 0 ||
          typeof body.idempotency_key !== 'string' || !body.idempotency_key.trim()) throw new Error('Ungültiger Auftrag')
      return request
    } catch { sessionStorage.removeItem(storageKey(sessionId)); return null }
  } catch { return null }
}

function persistHandregalRequest(request: HandregalRequest): boolean {
  try {
    const value = JSON.stringify(request)
    sessionStorage.setItem(storageKey(request.sessionId), value)
    return sessionStorage.getItem(storageKey(request.sessionId)) === value
  } catch { return false }
}

function removeHandregalRequest(request: HandregalRequest): boolean {
  try {
    const key = storageKey(request.sessionId)
    const stored = sessionStorage.getItem(key)
    if (stored === null || stored !== JSON.stringify(request)) return true
    sessionStorage.removeItem(key)
    return sessionStorage.getItem(key) === null
  }
  catch { return false }
}

function InventurZaehlenSession({ sessionId }: { sessionId: number }) {
  const detail = useInventurDetail(Number.isFinite(sessionId) ? sessionId : null)
  const recordCount = useRecordCount(sessionId)
  const { warehouse } = useAuth()

  const frozenCount = useRef<CountInput | null>(null)
  const [countFailure, setCountFailure] = useState<string | null>(null)
  const [phase, setPhase] = useState<Phase>({ step: 'idle' })
  const [manual, setManual] = useState('')
  const [banner, setBanner] = useState<string | null>(null)
  const [qtyInputs, setQtyInputs] = useState<Record<number, string>>({})
  const [pendingWarn, setPendingWarn] = useState<Record<number, string>>({})
  const [localEntries, setLocalEntries] = useState<Record<number, LocalDecision>>({})
  const [restoredRequest] = useState(() => readHandregalRequest(sessionId))
  const [handregalSku, setHandregalSku] = useState('')
  const [handregalSkuManual, setHandregalSkuManual] = useState(restoredRequest?.body.handregal.merchant_sku ?? '')
  const [handregalQty, setHandregalQty] = useState(restoredRequest ? String(restoredRequest.body.handregal.counted_qty) : '')
  const scanInputRef = useRef<HTMLInputElement>(null)
  // A-C6/C-I7 (Welle S2): ein Handregal-Sende-Versuch behaelt seinen Key ueber
  // Retries (Formular bleibt bei Fehlschlag unveraendert stehen); ein
  // erfolgreicher Send loescht ihn wieder, ein neuer Versuch bekommt neuen Key.
  const handregalRequest = useRef<HandregalRequest | null>(restoredRequest)
  const [handregalUnclear, setHandregalUnclear] = useState(restoredRequest !== null)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [pendingScan, setPendingScan] = useState<string | null>(null)

  const session = detail.data?.session
  const isClosed = session?.status === 'abgeschlossen'
  const progress = detail.data?.progress ?? { total: 0, counted: 0 }
  const pct = progress.total > 0 ? Math.round((progress.counted / progress.total) * 100) : 0
  const nextBarcode = detail.data?.openPlaces[0]?.barcode

  // R3-analoge Fokus-Haertung: Scan-Feld nach jedem Abschluss automatisch wieder aktiv,
  // damit der Wedge-Scanner ohne Zwischenklick weiterlaufen kann.
  useEffect(() => {
    if (!isClosed && phase.step === 'idle') scanInputRef.current?.focus()
  }, [isClosed, phase.step])

  const resetPlaceState = useCallback(() => {
    setQtyInputs({}); setPendingWarn({}); setLocalEntries({})
  }, [])

  const completePlace = useCallback(async (barcode: string) => {
    const r = await detail.refetch()
    const next = r.data?.openPlaces[0]?.barcode
    setBanner(`✓ ${barcode} erfasst — nächster Platz: ${next ?? '—'}`)
    setPhase({ step: 'idle' })
    setManual('')
    resetPlaceState()
  }, [detail, resetPlaceState])

  const performScan = useCallback(async (code: string) => {
    if (isClosed || frozenCount.current || recordCount.isPending) return
    const scan = classifyScanFrontend(code)
    if (scan.kind !== 'place') {
      toast.error(scan.kind === 'palette' ? 'Das ist ein Paletten-Code — hier den PLATZ-Code scannen' : `Unbekannter Code: ${code}`)
      return
    }
    try {
      const info = await fetchPlaceInfo(sessionId, scan.barcode)
      setBanner(null)
      resetPlaceState()
      // Neuer Scan = neuer logischer Sende-Versuch → neuer Key (kein Retry).
      setPhase({ step: 'place', barcode: scan.barcode, info, sendKey: newIdempotencyKey() })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Platz nicht gefunden')
    }
  }, [sessionId, isClosed, resetPlaceState, recordCount.isPending])

  const handleScan = useCallback((code: string) => {
    if (handregalUnclear || Object.keys(localEntries).length > 0 || Object.values(qtyInputs).some(v => v.trim() !== '')) { setPendingScan(code); return }
    void performScan(code)
  }, [handregalUnclear, localEntries, qtyInputs, performScan])
  useEffect(() => {
    if (!handregalUnclear && !(handregalRequest.current && recordCount.isPending)) return
    const unload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = '' }
    window.addEventListener('beforeunload', unload)
    return () => window.removeEventListener('beforeunload', unload)
  }, [handregalUnclear, recordCount.isPending])
  useWedgeScanner((code) => handleScan(code), !isClosed && !countFailure)

  const submitEntries = useCallback(async (barcode: string, entries: CountEntry[]) => {
    // Derselbe Key ueber alle Retries dieses Platz-Scans (phase.sendKey bleibt
    // stabil, solange der Platz nicht neu gescannt oder verlassen wurde).
    const key = phase.step === 'place' ? phase.sendKey : newIdempotencyKey()
    const hadUnknown = frozenCount.current !== null
    const body = frozenCount.current ?? { place_barcode: barcode, entries, idempotency_key: key }
    // Preserve a lost request across later refusals; a nack certifies only its own invocation.
    frozenCount.current = body
    try {
      await recordCount.mutateAsync(body)
      frozenCount.current = null; setCountFailure(null)
      await completePlace(barcode)
    } catch (error) {
      const reserved = error instanceof ApiError && error.details.code === 'recapture_reserved'
      if (hadUnknown || reserved || isTransportError(error)) {
        setCountFailure(error instanceof Error ? error.message : 'Ergebnis unbekannt — erneut senden')
      } else { frozenCount.current = null }
    }
  }, [recordCount, completePlace, phase])

  /** Sammelt eine Positions-Entscheidung lokal (letzter Wert je Position gewinnt —
   *  Korrektur vor dem Send ist erlaubt). Sobald JEDE aktive Position des Platzes
   *  eine Entscheidung hat, geht EIN Sammel-POST mit allen Entries raus (F2-Fix). */
  function applyDecision(barcode: string, positions: PositionRow[], positionId: number, decision: LocalDecision) {
    const merged = { ...localEntries, [positionId]: decision }
    setLocalEntries(merged)
    const scoped = positions.filter(p => p.inScope)
    if (scoped.every((p) => merged[p.positionId] !== undefined)) {
      const entries: CountEntry[] = scoped.map((p) => {
        const d = merged[p.positionId] as LocalDecision
        return d.kind === 'missing' ? { position_id: p.positionId, count_type: 'nicht_gefunden' } : d.kind === 'confirmed'
          ? { position_id: p.positionId, confirmed: true }
          : { position_id: p.positionId, counted_qty: d.qty }
      })
      void submitEntries(barcode, entries)
    }
  }

  function onConfirmed(pos: PositionRow, positions: PositionRow[], barcode: string) {
    applyDecision(barcode, positions, pos.positionId, { kind: 'confirmed' })
  }

  function onMengeErfassen(pos: PositionRow, positions: PositionRow[], barcode: string) {
    const raw = qtyInputs[pos.positionId] ?? ''
    const qty = Number(raw)
    if (raw.trim() === '' || !Number.isFinite(qty) || qty < 0) { toast.error('Bitte eine gültige Menge eingeben'); return }
    if (pos.expectedQty != null) {
      const warn = qtyWarning(pos.expectedQty, qty)
      if (warn) { setPendingWarn((p) => ({ ...p, [pos.positionId]: warn })); return }
    }
    applyDecision(barcode, positions, pos.positionId, { kind: 'counted', qty })
  }

  function onTrotzdemSpeichern(pos: PositionRow, positions: PositionRow[], barcode: string) {
    const raw = qtyInputs[pos.positionId] ?? ''
    const qty = Number(raw)
    setPendingWarn((p) => { const n = { ...p }; delete n[pos.positionId]; return n })
    applyDecision(barcode, positions, pos.positionId, { kind: 'counted', qty })
  }

  function onCancelWarn(positionId: number) {
    setPendingWarn((p) => { const n = { ...p }; delete n[positionId]; return n })
  }

  function onPlatzLeer(barcode: string) {
    const ok = window.confirm(`Platz ${barcode} wirklich als LEER melden? Erwartete Positionen gelten dann als fehlend.`)
    if (!ok) return
    // "Platz leer" ist Sofort-Send und ersetzt lokale Einzel-Entscheidungen der
    // Positionen dieses Platzes (Spec F2) — ein Entry statt des Sammel-POSTs.
    setLocalEntries({})
    void submitEntries(barcode, [{ empty: true }])
  }

  async function onHandregal() {
    let request = handregalRequest.current
    if (!request) {
      const sku = handregalSkuManual.trim() || handregalSku
      if (!sku) { toast.error('Artikel auswählen oder SKU eingeben'); return }
      const qty = Number(handregalQty)
      if (handregalQty.trim() === '' || !Number.isFinite(qty) || qty < 0) { toast.error('Bitte eine gültige Menge eingeben'); return }
      request = { sessionId, body: { handregal: { merchant_sku: sku, counted_qty: qty }, idempotency_key: newIdempotencyKey() } }
    }
    if (!persistHandregalRequest(request)) { toast.error(storageError); return }
    handregalRequest.current = request
    try {
      await recordCount.mutateAsync(request.body)
      const sent = request.body.handregal
      toast.success(`Handregal ${sent.merchant_sku}: ${sent.counted_qty} St. erfasst`)
      if (!removeHandregalRequest(request)) { setHandregalUnclear(true); toast.error(storageError); return }
      handregalRequest.current = null; setHandregalUnclear(false)
      setHandregalQty(''); setHandregalSkuManual('')
    } catch (error) {
      const rejected = error instanceof ApiError && !error.undecodable && (error.status === 400 || error.status === 422)
      if (rejected && removeHandregalRequest(request)) { handregalRequest.current = null; setHandregalUnclear(false) }
      else setHandregalUnclear(true)
    }
  }

  function discardHandregal() {
    if (handregalRequest.current && !removeHandregalRequest(handregalRequest.current)) { toast.error(storageError); return }
    handregalRequest.current = null; setHandregalUnclear(false); setDiscardOpen(false)
  }

  if (detail.isLoading) return <div className="p-4">Lade…</div>
  if (!detail.data || !session) {
    return (
      <div className="p-4">
        Session nicht gefunden. <Link className="text-primary underline" to="/warehouse/inventur">Zurück</Link>
      </div>
    )
  }

  if (!warehouse.count) {
    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="rounded-xl border border-line bg-panel p-6 text-center">
          <div className="text-lg font-semibold text-muted">
            Keine Berechtigung zum Zählen — dieses Gerät/Konto hat kein Zähl-Recht.
          </div>
          <div className="mt-4">
            <Link className="text-primary underline" to="/warehouse/inventur">← Zurück</Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      <Dialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <DialogContent><DialogHeader><DialogTitle>Handregal-Auftrag verwerfen?</DialogTitle><DialogDescription>Vorher im Inventur-Detail prüfen, ob die Zählung schon erfasst ist.</DialogDescription></DialogHeader>
          <DialogFooter><Button variant="outline" onClick={() => setDiscardOpen(false)}>Abbrechen</Button><Button onClick={discardHandregal}>Auftrag verwerfen</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={pendingScan !== null} onOpenChange={(open) => { if (!open) setPendingScan(null) }}><DialogContent><DialogHeader><DialogTitle>Neuen Platz scannen?</DialogTitle><DialogDescription>{handregalUnclear ? 'Das Ergebnis der Handregal-Zählung ist ungeklärt. Der Auftrag bleibt für die Wiederholung erhalten.' : 'Ungespeicherte Eingaben und Entscheidungen gehen verloren.'}</DialogDescription></DialogHeader><DialogFooter><Button variant="outline" onClick={() => setPendingScan(null)}>Abbrechen</Button><Button onClick={() => { const code = pendingScan; setPendingScan(null); if (code === '__back__') { setPhase({ step: 'idle' }); resetPlaceState() } else if (code) void performScan(code) }}>Weiter scannen</Button></DialogFooter></DialogContent></Dialog>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-bold">Zählen — Inventur #{sessionId}</h1>
        <Link className="text-primary underline text-sm" to={`/warehouse/inventur/${sessionId}`}>← Detail</Link>
      </div>

      <div className="rounded-xl border border-line bg-panel p-4 space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="font-semibold">Fortschritt</span>
          <span className="text-muted">{progress.counted} von {progress.total} Plätzen</span>
        </div>
        <div className="h-2 rounded-full bg-panel-2 overflow-hidden">
          <div className="h-full bg-[#108474]" style={{ width: `${pct}%` }} />
        </div>
        {nextBarcode && (
          <div className="text-sm">Nächster Platz: <span className="font-mono font-semibold">{nextBarcode}</span></div>
        )}
      </div>

      {isClosed && (
        <div className="rounded-xl border-2 border-line bg-panel p-4 text-center text-muted">
          Diese Inventur ist abgeschlossen — nur Ansicht, kein Zählen mehr möglich.
        </div>
      )}

      {isClosed && handregalUnclear && handregalRequest.current && (
        <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
          <div className="font-semibold">Ungeklärte Handregal-Zählung</div>
          <div>{handregalRequest.current.body.handregal.merchant_sku} · {handregalRequest.current.body.handregal.counted_qty} St.</div>
          <p className="text-sm text-muted">Session abgeschlossen – Ergebnis im Inventur-Detail prüfen</p>
          <div className="flex flex-wrap gap-2">
            <Button disabled={recordCount.isPending} onClick={() => void onHandregal()}>Erneut senden</Button>
            <Button variant="outline" disabled={recordCount.isPending} onClick={() => setDiscardOpen(true)}>Verwerfen</Button>
          </div>
        </div>
      )}

      {banner && (
        <div className="rounded-xl border-2 border-green-500 bg-green-50 dark:bg-green-950 p-4 text-center font-semibold text-green-700 dark:text-green-300">
          {banner}
        </div>
      )}

      {!isClosed && phase.step === 'idle' && (
        <div className="rounded-xl border-2 border-dashed border-line p-8 text-center space-y-3">
          <div className="text-2xl font-bold">Platz-Code scannen</div>
          <div className="text-muted">PDA-Scanner nutzen — oder Code eintippen</div>
          <div className="flex gap-2 justify-center">
            <Input
              ref={scanInputRef}
              className="w-56"
              placeholder="Platz-Code eintippen…"
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && manual) { void handleScan(manual); setManual('') } }}
            />
          </div>
        </div>
      )}

      {!isClosed && countFailure && <div className="rounded-xl border border-red-600 p-4 space-y-3">
        <p>{countFailure}</p>
        <Button disabled={recordCount.isPending} onClick={() => { const body = frozenCount.current; if (body?.place_barcode && body.entries) void submitEntries(body.place_barcode, body.entries) }}>Erneut senden</Button>
      </div>}
      {!isClosed && phase.step === 'place' && !countFailure && (
        <div className="space-y-3">
          <div className="rounded-xl border border-line bg-panel p-4 space-y-2">
            <div className="text-lg font-bold font-mono">{phase.barcode}</div>
            {!phase.info.inScope && (
              <div className="rounded bg-[#FFB300]/20 text-amber-900 dark:text-amber-200 px-2 py-1 text-sm font-semibold">
                Platz gehört nicht zu dieser Inventur
              </div>
            )}
            {phase.info.alreadyCounted && (
              <div className="rounded bg-[#FFB300]/20 text-amber-900 dark:text-amber-200 px-2 py-1 text-sm font-semibold">
                Platz wurde schon gezählt — neue Zählung ersetzt (Zweitzählung)
              </div>
            )}
            {phase.info.inScope && phase.info.positions.filter(p => p.inScope).length > 1 && (
              <div className="text-sm font-semibold">
                {Object.keys(localEntries).length} von {phase.info.positions.filter(p => p.inScope).length} Paletten erfasst
                — wird erst nach der letzten Palette gemeinsam gespeichert
              </div>
            )}
          </div>

          {phase.info.positions.length === 0 && (
            <div className="rounded-xl border border-line bg-panel p-4 text-center text-muted">
              Keine aktive Palette auf diesem Platz erkannt.
            </div>
          )}

          {phase.info.inScope && phase.info.positions.some(p => !p.inScope) && <details className="rounded border border-line bg-panel p-3 text-sm text-muted"><summary>Weitere Paletten außerhalb der Inventur ({phase.info.positions.filter(p => !p.inScope).length})</summary>{phase.info.positions.filter(p => !p.inScope).map(p => <div key={p.positionId}>{p.paletteNo ?? '—'} · <GrundartikelLink code={p.merchantSku} /></div>)}</details>}
          {phase.info.inScope && phase.info.positions.filter(p => p.inScope).map((pos) => {
            const decision = localEntries[pos.positionId]
            return (
            <div key={pos.positionId} className="rounded-xl border border-line bg-panel p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-muted">Zettel {pos.paletteNo ?? '—'}</div>
                <div className="flex gap-2 items-center">
                  <span
                    className={
                      'px-2 py-0.5 rounded text-xs font-semibold ' +
                      (pos.state === 'verschlossen'
                        ? 'bg-[#108474]/15 text-[#108474]'
                        : 'bg-[#FFB300]/20 text-amber-900 dark:text-amber-200')
                    }
                  >
                    {pos.state === 'verschlossen' ? 'verschlossen' : 'ausgepackt'}
                  </span>
                  <span
                    className={
                      'px-2 py-0.5 rounded text-xs font-semibold ' +
                      (decision
                        ? 'bg-green-500/15 text-green-700 dark:text-green-300'
                        : 'bg-panel-2 text-muted')
                    }
                  >
                    {decision
                      ? (decision.kind === 'confirmed' ? '✓ erfasst (stimmt)' : decision.kind === 'missing' ? 'Nicht gefunden' : `✓ erfasst (${decision.qty} ${pos.einheit === 'm' ? 'm' : 'St.'})`)
                      : 'offen'}
                  </span>
                </div>
              </div>
              <div className="text-xl font-bold"><GrundartikelLink code={pos.merchantSku} /></div>
              <div className="text-muted">{pos.itemName ?? '—'}</div>
              <div className="text-2xl font-bold">Erwartet: {pos.expectedQty ?? '—'} {pos.einheit === 'm' ? 'm' : 'St.'}</div>

              <div className="flex flex-wrap gap-2 items-center pt-2">
                {pos.state === 'verschlossen' && (
                  <Button
                    className="text-lg py-6 flex-1"
                    onClick={() => onConfirmed(pos, phase.info.positions, phase.barcode)}
                    disabled={recordCount.isPending}
                  >
                    Stimmt (Sichtvergleich)
                  </Button>
                )}
                <div className="flex gap-2 items-center flex-1">
                  <Input
                    type="number"
                    inputMode="numeric"
                    className="w-24 text-lg h-12"
                    placeholder="Menge"
                    value={qtyInputs[pos.positionId] ?? ''}
                    onChange={(e) => setQtyInputs((p) => ({ ...p, [pos.positionId]: e.target.value }))}
                  />
                  <Button
                    variant="outline"
                    className="text-lg py-6 flex-1"
                    onClick={() => onMengeErfassen(pos, phase.info.positions, phase.barcode)}
                    disabled={recordCount.isPending}
                  >
                    Menge erfassen
                  </Button>
                </div>
              </div>

              <Button variant="outline" disabled={recordCount.isPending} onClick={() => applyDecision(phase.barcode, phase.info.positions, pos.positionId, { kind: 'missing' })}>Nicht gefunden</Button>
              {pendingWarn[pos.positionId] && (
                <div className="rounded-lg border-2 border-amber-400 bg-amber-50 dark:bg-amber-950 p-3 space-y-2 text-center">
                  <div className="text-sm font-semibold text-amber-900 dark:text-amber-200">{pendingWarn[pos.positionId]}</div>
                  <div className="flex gap-2 justify-center">
                    <Button onClick={() => onTrotzdemSpeichern(pos, phase.info.positions, phase.barcode)} disabled={recordCount.isPending}>
                      Trotzdem speichern
                    </Button>
                    <Button variant="ghost" onClick={() => onCancelWarn(pos.positionId)}>Abbrechen</Button>
                  </div>
                </div>
              )}
            </div>
            )
          })}

          {phase.info.inScope && !phase.info.hasRegisteredPallets && phase.info.positions.length === 0 && <Button
            variant="outline"
            className="w-full text-lg py-6 border-2 border-red-600 text-red-700 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={() => onPlatzLeer(phase.barcode)}
            disabled={recordCount.isPending}
          >
            Platz ist LEER
          </Button>}

          <Button variant="ghost" className="w-full" onClick={() => { if (Object.keys(localEntries).length || Object.values(qtyInputs).some(v => v.trim() !== '')) setPendingScan('__back__'); else { setPhase({ step: 'idle' }); resetPlaceState() } }}>
            Zurück zum Scannen
          </Button>
        </div>
      )}

      {/* P2 (Welle 1b): optisch abgesetzt + unmissverstaendlich beschriftet.
          Vorfall 2026-07-26: Der Leiter wollte eine Palette korrigieren und trug die
          Menge hier ein — der alte Hinweis ("Menge direkt erfassen, ohne Platz-Scan")
          las sich wie die schnellere Alternative zur Platz-Zaehlung. Sie ist aber die
          ZWEITE HAELFTE: Paletten werden ausschliesslich ueber die Platz-Zaehlung
          korrigiert, das Handregal hat gar keine Position (inventur.ts:611). */}
      {!isClosed && session.mode === 'produkt' && (
        <div className="rounded-xl border-2 border-dashed border-line bg-panel/60 p-4 space-y-3">
          <div className="font-semibold">Handregal-Bestand · zusätzlich zur Paletten-Zählung</div>
          <div className="text-xs text-muted">
            Was im <span className="font-medium">Handpackregal</span> liegt — ohne Platz-Scan, weil das
            Handregal keine Palettenplätze hat.{' '}
            <span className="font-medium text-amber-700">
              Korrigiert KEINE Palette: Ein Palettenbestand ändert sich nur über die Platz-Zählung oben.
            </span>{' '}
            Beide Hälften gehören zu einer vollständigen Produkt-Inventur.
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <select
              className="border border-line rounded-md px-2 py-1.5 text-sm bg-panel flex-1 min-w-40 disabled:opacity-50"
              value={handregalSku}
              onChange={(e) => setHandregalSku(e.target.value)}
              disabled={handregalUnclear || recordCount.isPending || handregalSkuManual.trim() !== ''}
            >
              <option value="">Artikel wählen…</option>
              {(session.scope.merchant_skus ?? []).map((sku) => (
                <option key={sku} value={sku}>{sku}</option>
              ))}
            </select>
            <Input
              type="number"
              inputMode="numeric"
              className="w-24"
              placeholder="Menge"
              value={handregalQty}
              disabled={handregalUnclear || recordCount.isPending}
              onChange={(e) => setHandregalQty(e.target.value)}
            />
            <Button onClick={() => void onHandregal()} disabled={recordCount.isPending}>{handregalUnclear ? 'Erneut senden' : 'Menge erfassen'}</Button>
            {handregalUnclear && <Button variant="outline" disabled={recordCount.isPending} onClick={() => setDiscardOpen(true)}>Verwerfen</Button>}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input
              className="flex-1 min-w-40 font-mono text-sm"
              placeholder="oder exakte SKU eingeben (falls Artikel mehrdeutig/unbekannt)"
              value={handregalSkuManual}
              disabled={handregalUnclear || recordCount.isPending}
              onChange={(e) => setHandregalSkuManual(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  )
}
